"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import styles from "./BelysningOchDetaljer.module.css";
interface LightingItem {
  number: string;
  title: string;
  description: string;
}
const sectionImage = {
  src: "https://proffsbadrum.se/wp-content/uploads/2026/10/photorealistic-vertical-interior-photogr-a79d8b.png",
  alt: "Inspirationsbild: modernt skandinaviskt badrum i kvällsljus med infällda spotlights i taket, stor spegel med mjukt funktionsljus och varm indirekt LED-belysning under en vägghängd kommod",
  width: 1152,
  height: 1536
};
const lightingItems: LightingItem[] = [{
  number: "01",
  title: "Funktionellt allmänljus",
  description: "Riktade eller infällda spotlights i taket ger en bra grundbelysning som lyser upp hela rummet. Med dimmerfunktion kan du anpassa ljusstyrkan efter behov – från ett klart morgonljus till en mjukare och mer dämpad ton under kvällens bad."
}, {
  number: "02",
  title: "Skuggfritt funktionsljus",
  description: "Vid spegel och tvättställ är ljusets placering avgörande. Belysning framifrån eller på båda sidor om spegeln hjälper till att minska skuggor i ansiktet och ger bättre förutsättningar för rakning, sminkning och andra vardagsrutiner."
}, {
  number: "03",
  title: "Stämningsfullt accentljus",
  description: "Dolda LED-lister i hyllnischer eller under kommoden kan ge badrummet djup och en avkopplande spa-känsla. Indirekt belysning skapar en mjuk ljusmiljö och kan även fungera som behaglig orienteringsbelysning under natten."
}];
export default function BelysningOchDetaljer() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="belysning-och-detaljer" className={`${styles.belysningSection} text-foreground py-16 sm:py-20 md:py-24 px-4 sm:px-6 lg:px-8`}>
      <div className="max-w-7xl mx-auto" ref={ref}>
        <div className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {/* Centered header module */}
          <div className="text-center mb-16">
            <h2 className="font-default text-3xl md:text-5xl font-semibold text-foreground tracking-tight">
              Belysning och detaljer som gör skillnad
            </h2>
            <div className="mt-4 h-1 w-20 bg-primary mx-auto rounded-full" />
            <p className="mt-6 max-w-3xl mx-auto text-muted-foreground text-base md:text-lg leading-relaxed">
              En genomtänkt ljussättning förändrar hela upplevelsen av badrummet.
              Genom att kombinera belysning i olika nivåer skapar du en balans
              mellan funktionellt ljus för morgonrutinen och rofyllt
              stämningsljus på kvällen.
            </p>
          </div>

          {/* Split layout: lg:grid-cols-[45fr_55fr], image above on mobile */}
          <div className="grid grid-cols-1 lg:grid-cols-[45fr_55fr] gap-10 lg:gap-14 xl:gap-16 items-start">
            {/* Image Column */}
            <div className="w-full lg:sticky lg:top-24">
              <div className="aspect-[4/5] max-h-[28rem] sm:max-h-[32rem] lg:max-h-none lg:aspect-[3/4] w-full overflow-hidden rounded-xl bg-muted">
                <img alt={sectionImage.alt} width={sectionImage.width} height={sectionImage.height} loading="lazy" decoding="async" className="h-full w-full object-cover" src="https://proffsbadrum.se/wp-content/uploads/2026/10/proffsbadrum_belysning_513x648.webp" />
              </div>
            </div>

            {/* Vertically stacked text items */}
            <ol className="list-none m-0 p-0 flex flex-col">
              {lightingItems.map((item, index) => <li key={item.number} className={`py-8 md:py-10 ${index !== 0 ? "border-t border-border" : "pt-0 md:pt-0"}`}>
                  <span className="block text-sm font-semibold tracking-wide text-primary mb-2">
                    {item.number}
                  </span>
                  <h3 className="font-default text-xl md:text-2xl font-semibold text-foreground mb-3">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
                    {item.description}
                  </p>
                </li>)}
            </ol>
          </div>
        </div>
      </div>
    </section>;
}